// enum for depreciation methods
import Decimal from "decimal.js-light";

export enum DepreciationMethod {
  StraightLine = "Straight Line",
  DoubleDecliningBalance = "200% Declining Balance",
  DecliningBalance = "150% Declining Balance",
}

// enum for convention
export enum DepreciationConvention {
  HalfYear = "Half Year",
  MidMonth = "Mid Month",
  MidQuarter = "Mid Quarter",
}

interface DepreciationTable {
  [method: string]: {
    [convention: string]: {
      [life: number]: {
        [period: number]: number[]; // period is 0 for Half Year, 1-4 for Mid Quarter, 1-12 for Mid Month
      };
    };
  };
}

export const depreciationRates: DepreciationTable = {
  [DepreciationMethod.DoubleDecliningBalance]: {
    [DepreciationConvention.HalfYear]: {
      3: { 0: [33.33, 44.45, 14.81, 7.41] },
      5: { 0: [20.0, 32.0, 19.2, 11.52, 11.52, 5.76] },
      7: { 0: [14.29, 24.49, 17.49, 12.49, 8.93, 8.92, 8.93, 4.46] },
      10: { 0: [10.0, 18.0, 14.4, 11.52, 9.22, 7.37, 6.55, 6.55, 6.56, 6.55, 3.28] },
    },
    [DepreciationConvention.MidQuarter]: {
      3: {
        1: [58.33, 27.78, 12.35, 1.54],
        2: [41.67, 38.89, 14.14, 5.3],
        3: [25.0, 50.0, 16.67, 8.33],
        4: [8.33, 61.11, 20.37, 10.19],
      },
      5: {
        1: [35.0, 26.0, 15.6, 11.01, 11.01, 1.38],
        2: [25.0, 30.0, 18.0, 11.37, 11.37, 4.26],
        3: [15.0, 34.0, 20.4, 12.24, 11.3, 7.06],
        4: [5.0, 38.0, 22.8, 13.68, 10.94, 9.58],
      },
      7: {
        1: [25.0, 21.43, 15.31, 10.93, 8.75, 8.74, 8.75, 1.09],
        2: [17.85, 23.47, 16.76, 11.97, 8.87, 8.87, 8.87, 3.34],
        3: [10.71, 25.51, 18.22, 13.02, 9.3, 8.85, 8.86, 5.53],
        4: [3.57, 27.55, 19.68, 14.06, 10.04, 8.73, 8.73, 7.64],
      },
      10: {
        1: [17.5, 16.5, 13.2, 10.56, 8.45, 6.76, 6.55, 6.55, 6.56, 6.55, 0.82],
        2: [12.5, 17.5, 14.0, 11.2, 8.96, 7.17, 6.55, 6.55, 6.56, 6.55, 2.46],
        3: [7.5, 18.5, 14.8, 11.84, 9.47, 7.58, 6.55, 6.55, 6.56, 6.55, 4.1],
        4: [2.5, 19.5, 15.6, 12.48, 9.98, 7.99, 6.55, 6.55, 6.56, 6.55, 5.74],
      },
    },
  },
  [DepreciationMethod.DecliningBalance]: {
    [DepreciationConvention.HalfYear]: {
      15: {
        0: [5.0, 9.5, 8.55, 7.7, 6.93, 6.23, 5.9, 5.9, 5.91, 5.9, 5.91, 5.9, 5.91, 5.9, 5.91, 2.95],
      },
      20: {
        0: [
          3.75, 7.219, 6.677, 6.177, 5.713, 5.285, 4.888, 4.522, 4.462, 4.461, 4.462, 4.461, 4.462,
          4.461, 4.462, 4.461, 4.462, 4.461, 4.462, 4.461, 2.231,
        ],
      },
    },
    [DepreciationConvention.MidQuarter]: {
      15: {
        1: [
          8.75, 9.13, 8.21, 7.39, 6.65, 5.99, 5.9, 5.91, 5.9, 5.91, 5.9, 5.91, 5.9, 5.91, 5.9, 0.74,
        ],
        2: [
          6.25, 9.38, 8.44, 7.59, 6.83, 6.15, 5.91, 5.9, 5.91, 5.9, 5.91, 5.9, 5.91, 5.9, 5.91,
          2.21,
        ],
        3: [
          3.75, 9.63, 8.66, 7.8, 7.02, 6.31, 5.9, 5.9, 5.91, 5.9, 5.91, 5.9, 5.91, 5.9, 5.91, 3.69,
        ],
        4: [1.25, 9.88, 8.89, 8.0, 7.2, 6.48, 5.9, 5.9, 5.9, 5.91, 5.9, 5.91, 5.9, 5.91, 5.9, 5.17],
      },
      20: {
        1: [
          6.563, 7.0, 6.482, 5.996, 5.546, 5.13, 4.746, 4.459, 4.459, 4.459, 4.459, 4.46, 4.459,
          4.46, 4.459, 4.46, 4.459, 4.46, 4.459, 4.46, 0.565,
        ],
        2: [
          4.688, 7.148, 6.612, 6.116, 5.658, 5.233, 4.841, 4.478, 4.463, 4.463, 4.463, 4.463, 4.463,
          4.463, 4.462, 4.463, 4.462, 4.463, 4.462, 4.463, 1.673,
        ],
        3: [
          2.813, 7.289, 6.742, 6.237, 5.769, 5.336, 4.936, 4.566, 4.46, 4.46, 4.46, 4.46, 4.461,
          4.46, 4.461, 4.46, 4.461, 4.46, 4.461, 4.46, 2.788,
        ],
        4: [
          0.938, 7.43, 6.872, 6.357, 5.88, 5.439, 5.031, 4.654, 4.458, 4.458, 4.458, 4.458, 4.458,
          4.458, 4.458, 4.458, 4.458, 4.459, 4.458, 4.459, 3.901,
        ],
      },
    },
  },
  [DepreciationMethod.StraightLine]: {
    [DepreciationConvention.HalfYear]: {
      2.5: { 0: [20.0, 40.0, 40.0] },
      3: { 0: [16.67, 33.33, 33.33, 16.67] },
      3.5: { 0: [14.29, 28.57, 28.57, 28.57] },
      4: { 0: [12.5, 25.0, 25.0, 25.0, 12.5] },
      5: { 0: [10.0, 20.0, 20.0, 20.0, 20.0, 10.0] },
      6: { 0: [8.33, 16.67, 16.67, 16.67, 16.66, 16.67, 8.33] },
      6.5: { 0: [7.69, 15.39, 15.38, 15.39, 15.38, 15.39, 15.38] },
      7: { 0: [7.14, 14.29, 14.29, 14.28, 14.29, 14.28, 14.29, 7.14] },
      7.5: { 0: [6.67, 13.33, 13.33, 13.33, 13.34, 13.33, 13.34, 13.33] },
      8: { 0: [6.25, 12.5, 12.5, 12.5, 12.5, 12.5, 12.5, 12.5, 6.25] },
      8.5: { 0: [5.88, 11.77, 11.76, 11.77, 11.76, 11.77, 11.76, 11.77, 11.76] },
      9: { 0: [5.56, 11.11, 11.11, 11.11, 11.11, 11.11, 11.11, 11.11, 11.11, 5.56] },
      9.5: { 0: [5.26, 10.53, 10.53, 10.53, 10.52, 10.53, 10.52, 10.53, 10.52, 10.53] },
      10: { 0: [5.0, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 5.0] },
      10.5: { 0: [4.76, 9.52, 9.52, 9.53, 9.52, 9.53, 9.52, 9.53, 9.52, 9.53, 9.52] },
      11: { 0: [4.55, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 4.55] },
      11.5: { 0: [4.35, 8.7, 8.7, 8.69, 8.7, 8.69, 8.7, 8.69, 8.7, 8.69, 8.7, 8.69] },
      12: { 0: [4.17, 8.33, 8.33, 8.33, 8.33, 8.33, 8.34, 8.33, 8.34, 8.33, 8.34, 8.33, 4.17] },
      12.5: { 0: [4.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0] },
      13: { 0: [3.85, 7.69, 7.69, 7.69, 7.69, 7.69, 7.69, 7.69, 7.7, 7.69, 7.69, 7.7, 7.69, 3.85] },
      13.5: { 0: [3.7, 7.41, 7.41, 7.41, 7.41, 7.41, 7.41, 7.41, 7.41, 7.4, 7.41, 7.4, 7.41, 7.4] },
      14: {
        0: [
          3.57, 7.14, 7.14, 7.14, 7.14, 7.14, 7.14, 7.15, 7.14, 7.15, 7.14, 7.15, 7.14, 7.15, 3.57,
        ],
      },
      15: {
        0: [
          3.33, 6.67, 6.67, 6.67, 6.67, 6.67, 6.67, 6.66, 6.67, 6.66, 6.67, 6.66, 6.67, 6.66, 6.67,
          3.33,
        ],
      },
      16: {
        0: [
          3.13, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25,
          6.25, 3.12,
        ],
      },
      16.5: {
        0: [
          3.03, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06,
          6.06, 6.07,
        ],
      },
      17: {
        0: [
          2.94, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.89, 5.88, 5.89, 5.88, 5.89,
          5.88, 5.89, 2.94,
        ],
      },
      18: {
        0: [
          2.78, 5.56, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55, 5.56,
          5.55, 5.56, 5.55, 2.78,
        ],
      },
      19: {
        0: [
          2.63, 5.26, 5.26, 5.26, 5.26, 5.26, 5.26, 5.26, 5.27, 5.26, 5.27, 5.26, 5.27, 5.26, 5.27,
          5.26, 5.27, 5.26, 5.27, 2.63,
        ],
      },
      20: {
        0: [
          2.5, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0,
          5.0, 5.0, 2.5,
        ],
      },
      22: {
        0: [
          2.273, 4.545, 4.545, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546,
          4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 2.273,
        ],
      },
      24: {
        0: [
          2.083, 4.167, 4.167, 4.167, 4.167, 4.167, 4.167, 4.167, 4.167, 4.167, 4.166, 4.167, 4.166,
          4.167, 4.166, 4.167, 4.166, 4.167, 4.166, 4.167, 4.166, 4.167, 4.166, 4.167, 2.083,
        ],
      },
      25: {
        0: [
          2.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0,
          4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 2.0,
        ],
      },
      26.5: {
        0: [
          1.887, 3.774, 3.774, 3.774, 3.774, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773,
          3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773,
          3.774,
        ],
      },
      28: {
        0: [
          1.786, 3.571, 3.571, 3.571, 3.571, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572,
          3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571,
          3.572, 3.571, 1.786,
        ],
      },
      30: {
        0: [
          1.667, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.334,
          3.333, 3.334, 3.333, 3.334, 3.333, 3.334, 3.333, 3.334, 3.333, 3.334, 3.333, 3.334, 3.333,
          3.334, 3.333, 3.334, 3.333, 1.667,
        ],
      },
      35: {
        0: [
          1.429, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857,
          2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857,
          2.858, 2.857, 2.858, 2.857, 2.858, 2.857, 2.858, 2.858, 2.857, 1.429,
        ],
      },
      40: {
        0: [
          1.25, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5,
          2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5,
          2.5, 2.5, 2.5, 2.5, 1.25,
        ],
      },
      45: {
        0: [
          1.111, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222,
          2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222,
          2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 2.223,
          2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 1.111,
        ],
      },
      50: {
        0: [
          1.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0,
          2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0,
          2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 1.0,
        ],
      },
    },
    [DepreciationConvention.MidQuarter]: {
      2.5: {
        1: [35.0, 40.0, 25.0],
        2: [25.0, 40.0, 35.0],
        3: [15.0, 40.0, 40.0, 5.0],
        4: [5.0, 40.0, 40.0, 15.0],
      },
      3: {
        1: [29.17, 33.33, 33.33, 4.17],
        2: [20.83, 33.33, 33.34, 12.5],
        3: [12.5, 33.33, 33.34, 20.83],
        4: [4.17, 33.33, 33.33, 29.17],
      },
      3.5: {
        1: [25.0, 28.57, 28.57, 17.86],
        2: [17.86, 28.57, 28.57, 25.0],
        3: [10.71, 28.57, 28.57, 28.58, 3.57],
        4: [3.57, 28.57, 28.57, 28.57, 10.72],
      },
      4: {
        1: [21.88, 25.0, 25.0, 25.0, 3.12],
        2: [15.63, 25.0, 25.0, 25.0, 9.37],
        3: [9.38, 25.0, 25.0, 25.0, 15.62],
        4: [3.13, 25.0, 25.0, 25.0, 21.87],
      },
      5: {
        1: [17.5, 20.0, 20.0, 20.0, 20.0, 2.5],
        2: [12.5, 20.0, 20.0, 20.0, 20.0, 7.5],
        3: [7.5, 20.0, 20.0, 20.0, 20.0, 12.5],
        4: [2.5, 20.0, 20.0, 20.0, 20.0, 17.5],
      },
      6: {
        1: [14.58, 16.67, 16.67, 16.67, 16.66, 16.67, 2.08],
        2: [10.42, 16.67, 16.67, 16.66, 16.67, 16.66, 6.25],
        3: [6.25, 16.67, 16.67, 16.66, 16.67, 16.66, 10.42],
        4: [2.08, 16.67, 16.67, 16.67, 16.66, 16.67, 14.58],
      },
      6.5: {
        1: [13.46, 15.38, 15.39, 15.38, 15.39, 15.38, 9.62],
        2: [9.62, 15.38, 15.38, 15.39, 15.38, 15.39, 13.46],
        3: [5.77, 15.38, 15.39, 15.38, 15.39, 15.38, 15.39, 1.92],
        4: [1.92, 15.39, 15.38, 15.39, 15.38, 15.39, 15.38, 5.77],
      },
      7: {
        1: [12.5, 14.29, 14.28, 14.29, 14.28, 14.29, 14.28, 1.79],
        2: [8.93, 14.29, 14.28, 14.29, 14.28, 14.29, 14.28, 5.36],
        3: [5.36, 14.29, 14.28, 14.29, 14.28, 14.29, 14.28, 8.93],
        4: [1.79, 14.29, 14.28, 14.29, 14.28, 14.29, 14.28, 12.5],
      },
      7.5: {
        1: [11.67, 13.33, 13.33, 13.33, 13.34, 13.33, 13.34, 8.33],
        2: [8.33, 13.33, 13.33, 13.34, 13.33, 13.34, 13.33, 11.67],
        3: [5.0, 13.33, 13.33, 13.33, 13.34, 13.33, 13.34, 13.33, 1.67],
        4: [1.67, 13.33, 13.33, 13.33, 13.33, 13.34, 13.33, 13.34, 5.0],
      },
      8: {
        1: [10.94, 12.5, 12.5, 12.5, 12.5, 12.5, 12.5, 12.5, 1.56],
        2: [7.81, 12.5, 12.5, 12.5, 12.5, 12.5, 12.5, 12.5, 4.69],
        3: [4.69, 12.5, 12.5, 12.5, 12.5, 12.5, 12.5, 12.5, 7.81],
        4: [1.56, 12.5, 12.5, 12.5, 12.5, 12.5, 12.5, 12.5, 10.94],
      },
      8.5: {
        1: [10.29, 11.77, 11.76, 11.77, 11.76, 11.77, 11.76, 11.77, 7.35],
        2: [7.35, 11.77, 11.76, 11.77, 11.76, 11.77, 11.76, 11.77, 10.29],
        3: [4.41, 11.76, 11.77, 11.76, 11.77, 11.76, 11.77, 11.76, 11.77, 1.47],
        4: [1.47, 11.76, 11.77, 11.76, 11.77, 11.76, 11.77, 11.76, 11.77, 4.41],
      },
      9: {
        1: [9.72, 11.11, 11.11, 11.11, 11.11, 11.11, 11.11, 11.12, 11.11, 1.39],
        2: [6.94, 11.11, 11.11, 11.11, 11.11, 11.11, 11.11, 11.12, 11.11, 4.17],
        3: [4.17, 11.11, 11.11, 11.11, 11.11, 11.11, 11.11, 11.11, 11.11, 6.95],
        4: [1.39, 11.11, 11.11, 11.11, 11.11, 11.11, 11.11, 11.11, 11.11, 9.73],
      },
      9.5: {
        1: [9.21, 10.53, 10.53, 10.53, 10.52, 10.53, 10.52, 10.53, 10.52, 6.58],
        2: [6.58, 10.53, 10.53, 10.53, 10.52, 10.53, 10.52, 10.53, 10.52, 9.21],
        3: [3.95, 10.53, 10.53, 10.52, 10.53, 10.52, 10.53, 10.52, 10.53, 10.52, 1.32],
        4: [1.32, 10.53, 10.53, 10.52, 10.53, 10.52, 10.53, 10.52, 10.53, 10.52, 3.95],
      },
      10: {
        1: [8.75, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 1.25],
        2: [6.25, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 3.75],
        3: [3.75, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 6.25],
        4: [1.25, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 10.0, 8.75],
      },
      10.5: {
        1: [8.33, 9.52, 9.52, 9.53, 9.52, 9.53, 9.52, 9.53, 9.52, 9.53, 5.95],
        2: [5.95, 9.52, 9.52, 9.53, 9.52, 9.53, 9.52, 9.53, 9.52, 9.53, 8.33],
        3: [3.57, 9.52, 9.52, 9.52, 9.53, 9.52, 9.53, 9.52, 9.53, 9.52, 9.53, 1.19],
        4: [1.19, 9.52, 9.52, 9.52, 9.53, 9.52, 9.53, 9.52, 9.53, 9.52, 9.53, 3.57],
      },
      11: {
        1: [7.95, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.1, 9.09, 1.14],
        2: [5.68, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.1, 3.41],
        3: [3.41, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.1, 5.68],
        4: [1.14, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 9.09, 7.96],
      },
      11.5: {
        1: [7.61, 8.7, 8.7, 8.69, 8.7, 8.69, 8.7, 8.69, 8.7, 8.69, 8.7, 5.43],
        2: [5.43, 8.7, 8.7, 8.7, 8.69, 8.7, 8.69, 8.7, 8.69, 8.7, 8.69, 7.61],
        3: [3.26, 8.7, 8.7, 8.69, 8.7, 8.69, 8.7, 8.69, 8.7, 8.69, 8.7, 8.69, 1.09],
        4: [1.09, 8.7, 8.69, 8.7, 8.69, 8.7, 8.69, 8.7, 8.69, 8.7, 8.69, 8.7, 3.26],
      },
      12: {
        1: [7.29, 8.33, 8.33, 8.33, 8.33, 8.34, 8.33, 8.34, 8.33, 8.34, 8.33, 8.34, 1.04],
        2: [5.21, 8.33, 8.33, 8.33, 8.33, 8.33, 8.34, 8.33, 8.34, 8.33, 8.34, 8.33, 3.13],
        3: [3.13, 8.33, 8.33, 8.33, 8.33, 8.33, 8.34, 8.33, 8.34, 8.33, 8.34, 8.33, 5.21],
        4: [1.04, 8.33, 8.33, 8.33, 8.33, 8.34, 8.33, 8.34, 8.33, 8.34, 8.33, 8.34, 7.29],
      },
      12.5: {
        1: [7.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 5.0],
        2: [5.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 7.0],
        3: [3.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 1.0],
        4: [1.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 8.0, 3.0],
      },
      13: {
        1: [6.73, 7.69, 7.69, 7.69, 7.69, 7.69, 7.69, 7.69, 7.7, 7.69, 7.7, 7.69, 7.7, 0.96],
        2: [4.81, 7.69, 7.69, 7.69, 7.69, 7.69, 7.69, 7.69, 7.69, 7.7, 7.69, 7.7, 7.69, 2.89],
        3: [2.88, 7.69, 7.69, 7.69, 7.69, 7.69, 7.69, 7.7, 7.69, 7.7, 7.69, 7.7, 7.69, 4.81],
        4: [0.96, 7.69, 7.69, 7.69, 7.69, 7.69, 7.69, 7.69, 7.7, 7.69, 7.7, 7.69, 7.7, 6.73],
      },
      13.5: {
        1: [6.48, 7.41, 7.41, 7.41, 7.41, 7.41, 7.41, 7.41, 7.4, 7.41, 7.4, 7.41, 7.4, 4.63],
        2: [4.63, 7.41, 7.41, 7.41, 7.41, 7.41, 7.41, 7.41, 7.4, 7.41, 7.4, 7.41, 7.4, 6.48],
        3: [2.78, 7.41, 7.41, 7.41, 7.41, 7.41, 7.41, 7.4, 7.41, 7.4, 7.41, 7.4, 7.41, 7.4, 0.93],
        4: [0.93, 7.41, 7.41, 7.41, 7.41, 7.41, 7.41, 7.4, 7.41, 7.4, 7.41, 7.4, 7.41, 7.4, 2.78],
      },
      14: {
        1: [
          6.25, 7.14, 7.14, 7.14, 7.14, 7.14, 7.14, 7.15, 7.14, 7.15, 7.14, 7.15, 7.14, 7.15, 0.89,
        ],
        2: [
          4.46, 7.14, 7.14, 7.14, 7.14, 7.14, 7.15, 7.14, 7.15, 7.14, 7.15, 7.14, 7.15, 7.14, 2.68,
        ],
        3: [
          2.68, 7.14, 7.14, 7.14, 7.14, 7.14, 7.14, 7.14, 7.15, 7.14, 7.15, 7.14, 7.15, 7.14, 4.47,
        ],
        4: [
          0.89, 7.14, 7.14, 7.14, 7.14, 7.14, 7.14, 7.15, 7.14, 7.15, 7.14, 7.15, 7.14, 7.15, 6.25,
        ],
      },
      15: {
        1: [
          5.83, 6.67, 6.67, 6.67, 6.67, 6.67, 6.67, 6.66, 6.67, 6.66, 6.67, 6.66, 6.67, 6.66, 6.67,
          0.83,
        ],
        2: [
          4.17, 6.67, 6.67, 6.67, 6.67, 6.67, 6.66, 6.67, 6.66, 6.67, 6.66, 6.67, 6.66, 6.67, 6.66,
          2.5,
        ],
        3: [
          2.5, 6.67, 6.67, 6.67, 6.67, 6.67, 6.66, 6.67, 6.66, 6.67, 6.66, 6.67, 6.66, 6.67, 6.66,
          4.17,
        ],
        4: [
          0.83, 6.67, 6.67, 6.67, 6.67, 6.67, 6.67, 6.66, 6.67, 6.66, 6.67, 6.66, 6.67, 6.66, 6.67,
          5.83,
        ],
      },
      16: {
        1: [
          5.47, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25,
          6.25, 0.78,
        ],
        2: [
          3.91, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25,
          6.25, 2.34,
        ],
        3: [
          2.34, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25,
          6.25, 3.91,
        ],
        4: [
          0.78, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25, 6.25,
          6.25, 5.47,
        ],
      },
      16.5: {
        1: [
          5.3, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06,
          6.07, 3.79,
        ],
        2: [
          3.79, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06,
          6.06, 5.31,
        ],
        3: [
          2.27, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06,
          6.07, 6.06, 0.76,
        ],
        4: [
          0.76, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06, 6.06,
          6.06, 6.07, 2.27,
        ],
      },
      17: {
        1: [
          5.15, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.89, 5.88, 5.89, 5.88,
          5.89, 5.88, 0.74,
        ],
        2: [
          3.68, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.89, 5.88, 5.89, 5.88,
          5.89, 5.88, 2.21,
        ],
        3: [
          2.21, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.89, 5.88, 5.89, 5.88,
          5.89, 5.88, 3.68,
        ],
        4: [
          0.74, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.88, 5.89, 5.88, 5.89, 5.88,
          5.89, 5.88, 5.15,
        ],
      },
      18: {
        1: [
          4.86, 5.56, 5.56, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55,
          5.56, 5.55, 5.56, 0.69,
        ],
        2: [
          3.47, 5.56, 5.56, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55,
          5.56, 5.55, 5.56, 2.08,
        ],
        3: [
          2.08, 5.56, 5.56, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55,
          5.56, 5.55, 5.56, 3.47,
        ],
        4: [
          0.69, 5.56, 5.56, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55, 5.56, 5.55,
          5.56, 5.55, 5.56, 4.86,
        ],
      },
      19: {
        1: [
          4.61, 5.26, 5.26, 5.26, 5.26, 5.26, 5.26, 5.26, 5.26, 5.27, 5.26, 5.27, 5.26, 5.27, 5.26,
          5.27, 5.26, 5.27, 5.26, 0.66,
        ],
        2: [
          3.29, 5.26, 5.26, 5.26, 5.26, 5.26, 5.26, 5.26, 5.27, 5.26, 5.27, 5.26, 5.27, 5.26, 5.27,
          5.26, 5.27, 5.26, 5.27, 1.97,
        ],
        3: [
          1.97, 5.26, 5.26, 5.26, 5.26, 5.26, 5.26, 5.26, 5.27, 5.26, 5.27, 5.26, 5.27, 5.26, 5.27,
          5.26, 5.27, 5.26, 5.27, 3.29,
        ],
        4: [
          0.66, 5.26, 5.26, 5.26, 5.26, 5.26, 5.26, 5.26, 5.26, 5.27, 5.26, 5.27, 5.26, 5.27, 5.26,
          5.27, 5.26, 5.27, 5.26, 4.61,
        ],
      },
      20: {
        1: [
          4.375, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0,
          5.0, 5.0, 5.0, 0.625,
        ],
        2: [
          3.125, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0,
          5.0, 5.0, 5.0, 1.875,
        ],
        3: [
          1.875, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0,
          5.0, 5.0, 5.0, 3.125,
        ],
        4: [
          0.625, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0, 5.0,
          5.0, 5.0, 5.0, 4.375,
        ],
      },
      22: {
        1: [
          3.977, 4.545, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545,
          4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 0.568,
        ],
        2: [
          2.841, 4.545, 4.545, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546,
          4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 1.705,
        ],
        3: [
          1.705, 4.545, 4.545, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546,
          4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 2.841,
        ],
        4: [
          0.568, 4.545, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545,
          4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 4.545, 4.546, 3.977,
        ],
      },
      24: {
        1: [
          3.646, 4.167, 4.167, 4.167, 4.167, 4.167, 4.167, 4.167, 4.167, 4.166, 4.167, 4.166, 4.167,
          4.166, 4.167, 4.166, 4.167, 4.166, 4.167, 4.166, 4.167, 4.166, 4.167, 4.166, 0.521,
        ],
        2: [
          2.604, 4.167, 4.167, 4.167, 4.167, 4.167, 4.167, 4.167, 4.167, 4.167, 4.166, 4.167, 4.166,
          4.167, 4.166, 4.167, 4.166, 4.167, 4.166, 4.167, 4.166, 4.167, 4.166, 4.167, 1.562,
        ],
        3: [
          1.563, 4.167, 4.167, 4.167, 4.167, 4.167, 4.167, 4.167, 4.166, 4.167, 4.166, 4.167, 4.166,
          4.167, 4.166, 4.167, 4.166, 4.167, 4.166, 4.167, 4.166, 4.167, 4.166, 4.167, 2.604,
        ],
        4: [
          0.521, 4.167, 4.167, 4.167, 4.167, 4.167, 4.167, 4.167, 4.167, 4.166, 4.167, 4.166, 4.167,
          4.166, 4.167, 4.166, 4.167, 4.166, 4.167, 4.166, 4.167, 4.166, 4.167, 4.166, 3.646,
        ],
      },
      25: {
        1: [
          3.5, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0,
          4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 0.5,
        ],
        2: [
          2.5, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0,
          4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 1.5,
        ],
        3: [
          1.5, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0,
          4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 2.5,
        ],
        4: [
          0.5, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0,
          4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 4.0, 3.5,
        ],
      },
      26.5: {
        1: [
          3.302, 3.774, 3.774, 3.774, 3.774, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773,
          3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774,
          2.358,
        ],
        2: [
          2.358, 3.774, 3.774, 3.774, 3.774, 3.774, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774,
          3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773,
          3.302,
        ],
        3: [
          1.415, 3.774, 3.774, 3.774, 3.774, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773,
          3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774,
          3.773, 0.472,
        ],
        4: [
          0.472, 3.774, 3.774, 3.774, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774,
          3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773, 3.774, 3.773,
          3.774, 1.415,
        ],
      },
      28: {
        1: [
          3.125, 3.571, 3.571, 3.571, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571,
          3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572,
          3.571, 3.572, 0.446,
        ],
        2: [
          2.232, 3.571, 3.571, 3.571, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571,
          3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572,
          3.571, 3.572, 1.339,
        ],
        3: [
          1.339, 3.571, 3.571, 3.571, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571,
          3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572,
          3.571, 3.572, 2.232,
        ],
        4: [
          0.446, 3.571, 3.571, 3.571, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571,
          3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572, 3.571, 3.572,
          3.571, 3.572, 3.125,
        ],
      },
      30: {
        1: [
          2.917, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.334,
          3.333, 3.334, 3.333, 3.334, 3.333, 3.334, 3.333, 3.334, 3.333, 3.334, 3.333, 3.334, 3.333,
          3.334, 3.333, 3.334, 3.333, 0.417,
        ],
        2: [
          2.083, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.334, 3.333,
          3.334, 3.333, 3.334, 3.333, 3.334, 3.333, 3.334, 3.333, 3.334, 3.333, 3.334, 3.333, 3.334,
          3.333, 3.334, 3.333, 3.334, 1.25,
        ],
        3: [
          1.25, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.334, 3.333,
          3.334, 3.333, 3.334, 3.333, 3.334, 3.333, 3.334, 3.333, 3.334, 3.333, 3.334, 3.333, 3.334,
          3.333, 3.334, 3.333, 3.334, 2.083,
        ],
        4: [
          0.417, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.333, 3.334,
          3.333, 3.334, 3.333, 3.334, 3.333, 3.334, 3.333, 3.334, 3.333, 3.334, 3.333, 3.334, 3.333,
          3.334, 3.333, 3.334, 3.333, 2.917,
        ],
      },
      35: {
        1: [
          2.5, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857,
          2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857,
          2.858, 2.857, 2.858, 2.857, 2.858, 2.857, 2.858, 2.857, 2.858, 0.357,
        ],
        2: [
          1.786, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857,
          2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857,
          2.857, 2.858, 2.857, 2.858, 2.857, 2.858, 2.857, 2.858, 2.857, 1.072,
        ],
        3: [
          1.071, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857,
          2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.858,
          2.857, 2.858, 2.857, 2.858, 2.857, 2.858, 2.857, 2.858, 2.857, 1.786,
        ],
        4: [
          0.357, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857,
          2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857, 2.857,
          2.858, 2.857, 2.858, 2.857, 2.858, 2.857, 2.858, 2.857, 2.858, 2.5,
        ],
      },
      40: {
        1: [
          2.188, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5,
          2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5,
          2.5, 2.5, 2.5, 2.5, 2.5, 0.312,
        ],
        2: [
          1.563, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5,
          2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5,
          2.5, 2.5, 2.5, 2.5, 2.5, 0.937,
        ],
        3: [
          0.938, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5,
          2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5,
          2.5, 2.5, 2.5, 2.5, 2.5, 1.562,
        ],
        4: [
          0.313, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5,
          2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5, 2.5,
          2.5, 2.5, 2.5, 2.5, 2.5, 2.187,
        ],
      },
      45: {
        1: [
          1.944, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222,
          2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.223,
          2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 2.222,
          2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 0.278,
        ],
        2: [
          1.389, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222,
          2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222,
          2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 2.223,
          2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 0.833,
        ],
        3: [
          0.833, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222,
          2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222,
          2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 2.223,
          2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 1.389,
        ],
        4: [
          0.278, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222,
          2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222, 2.222,
          2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 2.223, 2.222,
          2.223, 2.222, 2.223, 2.222, 2.223, 2.222, 1.945,
        ],
      },
      50: {
        1: [
          1.75, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0,
          2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0,
          2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 0.25,
        ],
        2: [
          1.25, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0,
          2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0,
          2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 0.75,
        ],
        3: [
          0.75, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0,
          2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0,
          2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 1.25,
        ],
        4: [
          0.25, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0,
          2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0,
          2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 2.0, 1.75,
        ],
      },
    },
    [DepreciationConvention.MidMonth]: {
      27.5: {
        1: [
          3.485, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.637, 3.636, 3.637, 3.636,
          3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637,
          3.636, 1.97,
        ],
        2: [
          3.182, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.637, 3.636, 3.637, 3.636,
          3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637,
          3.636, 2.273,
        ],
        3: [
          2.879, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.637, 3.636, 3.637, 3.636,
          3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637,
          3.636, 2.576,
        ],
        4: [
          2.576, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.637, 3.636, 3.637, 3.636,
          3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637,
          3.636, 2.879,
        ],
        5: [
          2.273, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.637, 3.636, 3.637, 3.636,
          3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637,
          3.636, 3.182,
        ],
        6: [
          1.97, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.637, 3.636, 3.637, 3.636,
          3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637,
          3.636, 3.485,
        ],
        7: [
          1.667, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.637, 3.636, 3.637,
          3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636,
          3.637, 3.636, 0.152,
        ],
        8: [
          1.364, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.637, 3.636, 3.637,
          3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636,
          3.637, 3.636, 0.455,
        ],
        9: [
          1.061, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.637, 3.636, 3.637,
          3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636,
          3.637, 3.636, 0.758,
        ],
        10: [
          0.758, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.637, 3.636, 3.637,
          3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636,
          3.637, 3.636, 1.061,
        ],
        11: [
          0.455, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.637, 3.636, 3.637,
          3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636,
          3.637, 3.636, 1.364,
        ],
        12: [
          0.152, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.636, 3.637, 3.636, 3.637,
          3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636, 3.637, 3.636,
          3.637, 3.636, 1.667,
        ],
      },
      31.5: {
        1: [
          3.042, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174,
          3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175,
          3.174, 3.175, 3.174, 3.175, 3.174, 1.72,
        ],
        2: [
          2.778, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175,
          3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174,
          3.175, 3.174, 3.175, 3.174, 3.175, 1.984,
        ],
        3: [
          2.513, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174,
          3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175,
          3.174, 3.175, 3.174, 3.175, 3.174, 2.249,
        ],
        4: [
          2.249, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175,
          3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174,
          3.175, 3.174, 3.175, 3.174, 3.175, 2.513,
        ],
        5: [
          1.984, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174,
          3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175,
          3.174, 3.175, 3.174, 3.175, 3.174, 2.778,
        ],
        6: [
          1.72, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175,
          3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174,
          3.175, 3.174, 3.175, 3.174, 3.175, 3.042,
        ],
        7: [
          1.455, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174,
          3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175,
          3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 0.132,
        ],
        8: [
          1.19, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.174, 3.175, 3.174, 3.175,
          3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174,
          3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 0.397,
        ],
        9: [
          0.926, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174,
          3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175,
          3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 0.661,
        ],
        10: [
          0.661, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.174, 3.175, 3.174, 3.175,
          3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174,
          3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 0.926,
        ],
        11: [
          0.397, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174,
          3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175,
          3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 1.19,
        ],
        12: [
          0.132, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.175, 3.174, 3.175, 3.174, 3.175,
          3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 3.175, 3.174,
          3.175, 3.174, 3.175, 3.174, 3.175, 3.174, 1.455,
        ],
      },
      39: {
        1: [
          2.461, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          0.107,
        ],
        2: [
          2.247, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          0.321,
        ],
        3: [
          2.033, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          0.535,
        ],
        4: [
          1.819, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          0.749,
        ],
        5: [
          1.605, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          0.963,
        ],
        6: [
          1.391, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          1.177,
        ],
        7: [
          1.177, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          1.391,
        ],
        8: [
          0.963, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          1.605,
        ],
        9: [
          0.749, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          1.819,
        ],
        10: [
          0.535, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.033,
        ],
        11: [
          0.321, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.247,
        ],
        12: [
          0.107, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564,
          2.461,
        ],
      },
    },
  },
};

export function truncateToTwoDecimals(number: number) {
  // Multiply the number by 100 to shift the decimal two places to the right.
  const multiplied = number * 100;

  // Use Math.trunc() to remove any fractional part, effectively truncating.
  const truncated = Math.trunc(multiplied);

  // Divide by 100 to shift the decimal back to its original position.
  return truncated / 100;
}

// Helper function
export const getDepreciationRates = (
  method: DepreciationMethod,
  convention: DepreciationConvention,
  life: number,
  period: number
): number[] | undefined => {
  return depreciationRates[method]?.[convention]?.[life]?.[period];
};

export const getAvailableLives = (
  method: DepreciationMethod,
  convention: DepreciationConvention
): number[] => {
  return Object.keys(depreciationRates[method]?.[convention] || {}).map(k => Number(k));
};

export const getAvailablePeriods = (
  method: DepreciationMethod,
  convention: DepreciationConvention,
  life: number
): number[] => {
  return Object.keys(depreciationRates[method]?.[convention]?.[life] || {}).map(k => Number(k));
};

const getMidQuarterFraction = (date: Date): Decimal => {
  const quarter = getMidQuarter(date);
  switch (quarter.toNumber()) {
    case 1:
      return new Decimal(0.875);
    case 2:
      return new Decimal(0.625);
    case 3:
      return new Decimal(0.375);
    case 4:
      return new Decimal(0.125);
    default:
      return new Decimal(0);
  }
};

const getMidMonth = (date: Date): Decimal => {
  const month = date.getUTCMonth();
  return new Decimal(month + 1); // 0-based month to 1-based quarter
};

const getMidQuarter = (date: Date): Decimal => {
  const month = date.getUTCMonth();
  return new Decimal(Math.floor(month / 3) + 1); // 0-based month to 1-based quarter
};

const getMidMonthFraction = (date: Date): Decimal => {
  const month = date.getUTCMonth() + 1;
  const monthsRemaining = 12 - month + 0.5;
  return new Decimal(monthsRemaining / 12);
};

export const calculateDepreciation = (
  basis: number,
  placedInService: Date,
  life: number,
  method: DepreciationMethod,
  convention: DepreciationConvention
) => {
  const accumulatedDepreciationYears: number[] = [];
  const unrecoveredBasisYears: number[] = [];
  const currentDepreciationYears: number[] = [];
  let period = new Decimal(0);

  // Convention fraction (first year only)
  let conventionFraction = new Decimal(1);
  if (convention === DepreciationConvention.HalfYear) conventionFraction = new Decimal(0.5);
  else if (convention === DepreciationConvention.MidMonth) {
    period = getMidMonth(placedInService);
    conventionFraction = getMidMonthFraction(placedInService);
  } else if (convention === DepreciationConvention.MidQuarter) {
    period = getMidQuarter(placedInService);
    conventionFraction = getMidQuarterFraction(placedInService);
  }

  // Rate setup
  let rate = new Decimal(0);
  switch (method) {
    case DepreciationMethod.StraightLine:
      rate = new Decimal(1.0).div(life);
      break;
    case DepreciationMethod.DoubleDecliningBalance:
      rate = new Decimal(2.0).div(life);
      break;
    case DepreciationMethod.DecliningBalance:
      rate = new Decimal(1.5).div(life);
      break;
  }

  let remainingBasis = new Decimal(basis);
  let remainingBasisRounded = new Decimal(basis);
  let accumulated = new Decimal(0);
  let accumulatedRounded = new Decimal(0);
  let year = 0;

  while (accumulatedRounded.lt(basis)) {
    let depreciation = new Decimal(0);

    if (method === DepreciationMethod.StraightLine) {
      depreciation = new Decimal(basis).div(life);
    } else {
      const remainingYears = new Decimal(life).minus(new Decimal(year));
      const dbDep = remainingBasis.times(rate);
      const slDep = remainingBasis.div(
        remainingYears.add(new Decimal(1).minus(conventionFraction))
      ); // remaining-life SL

      // Switch if SL > DB
      depreciation = dbDep;
      if (slDep.gt(dbDep)) {
        depreciation = slDep;
      }
    }

    // Apply first-year convention fraction
    if (year === 0) depreciation = depreciation.times(conventionFraction);

    // Prevent over-depreciation
    if (accumulated.plus(depreciation).gt(basis)) {
      depreciation = new Decimal(basis).minus(accumulatedRounded);
    }

    // Store
    accumulated = accumulated.plus(depreciation);
    accumulatedRounded = accumulatedRounded.plus(depreciation.toDecimalPlaces(2).toNumber());

    currentDepreciationYears.push(depreciation.toDecimalPlaces(2).toNumber());

    remainingBasis = new Decimal(basis).minus(accumulated).lt(0)
      ? new Decimal(0)
      : new Decimal(basis).minus(accumulated);

    remainingBasisRounded = new Decimal(basis).minus(accumulatedRounded).lt(0)
      ? new Decimal(0)
      : new Decimal(basis).minus(accumulatedRounded);

    accumulatedDepreciationYears.push(accumulatedRounded.toNumber());
    unrecoveredBasisYears.push(remainingBasisRounded.toNumber());

    year++;

    if (year > life + 10) {
      // Safety break to prevent infinite loops
      break;
    }
  }
  return {
    accumulated: accumulatedDepreciationYears,
    unrecoveredBasis: unrecoveredBasisYears,
    current: currentDepreciationYears,
    period: period.toNumber(),
  };
};
